import { mande, type MandeError } from 'mande'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useTenant } from '@/composables/useTenant'

interface Category {
  id: string
  title: string
  slug: string
  type?: string
  image_url?: string
  items?: Category[]
  categories: Category[]
}

export function useCategoriesBySlug(slug: string) {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/category`)
  const language = useI18n().locale.value.split('-')[0]
  const categories = ref<Category | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchCategories = async () => {
    try {
      const response = await api.get<{ payload: Category }>(`tree/${slug}`, {
        headers: {
          'Accept-Language': language,
        },
        query: {
          tenant: useTenant().value,
        },
      })
      categories.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  return {
    categories,
    fetchCategories,
    loading,
    error,
  }
}

export function useRootCategories() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/category`)
  const language = useI18n().locale.value.split('-')[0]
  const rootCategories = ref<Category[] | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchRootCategories = async () => {
    try {
      const response = await api.get<{ payload: Category[] }>(`root`, {
        headers: {
          'Accept-Language': language,
        },
        query: {
          tenant: useTenant().value,
        },
      })
      rootCategories.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  fetchRootCategories().then(() => {})

  return {
    rootCategories,
    loading,
    error,
  }
}

export function useRootCategoriesWithSubItems() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/category`)
  const language = useI18n().locale.value.split('-')[0]
  const rootCategoriesWithSubItems = ref<Category[] | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchRootCategoriesWithSubItems = async () => {
    try {
      const response = await api.get<{ payload: Category[] }>(`root/children`, {
        headers: {
          'Accept-Language': language,
        },
        query: {
          tenant: useTenant().value,
        },
      })
      rootCategoriesWithSubItems.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  fetchRootCategoriesWithSubItems().then(() => {})

  return {
    rootCategoriesWithSubItems,
    loading,
    error,
  }
}
